import { useQuery } from "@tanstack/react-query";
import {
  ConciergeBell,
  Crown,
  Headset,
  Mail,
  Plus,
  Send,
  Terminal,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { useMemo, type ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";

import { FunnelShell } from "../funnel/shared";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { DealCard, Role, Sale } from "../types";

type Stats = { active: number; noCall: number; wonMonth: number };
type Kind = "owner" | "developer" | "admin" | "rop" | "manager";

// Each role gets its own window: colour, pattern, icon and what it shows
const LOOK: Record<
  Kind,
  {
    roof: string;
    pattern: string;
    icon: LucideIcon;
    avatar: string;
    badge: string;
  }
> = {
  owner: {
    roof: "bg-[linear-gradient(135deg,#b97a17,#e3ac45_55%,#f3d58f)]",
    pattern:
      "bg-[radial-gradient(circle_at_20%_30%,rgba(255,255,255,0.35)_0,transparent_35%),radial-gradient(circle_at_80%_10%,rgba(255,255,255,0.25)_0,transparent_30%)]",
    icon: Crown,
    avatar: "bg-[#fbefd2] text-[#8a5a0b]",
    badge: "bg-[#3d2a00] text-[#f3d58f]",
  },
  developer: {
    roof: "bg-[linear-gradient(135deg,#2b3540,#47535f)]",
    pattern:
      "bg-[linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] bg-[size:16px_16px]",
    icon: Terminal,
    avatar: "bg-[#2b3540] text-white font-mono",
    badge: "bg-white/90 text-[#2b3540] font-mono",
  },
  admin: {
    roof: "bg-[linear-gradient(135deg,#3d6f8a,#7aa9c2)]",
    pattern:
      "bg-[repeating-linear-gradient(135deg,transparent_0,transparent_10px,rgba(255,255,255,0.1)_10px,rgba(255,255,255,0.1)_12px)]",
    icon: ConciergeBell,
    avatar: "bg-[#e3eef4] text-[#2f5d73]",
    badge: "bg-white/90 text-[#2f5d73]",
  },
  rop: {
    roof: "bg-[linear-gradient(135deg,#1f4434,#2f5d46)]",
    pattern:
      "bg-[repeating-linear-gradient(90deg,transparent_0,transparent_18px,rgba(255,255,255,0.12)_18px,rgba(255,255,255,0.12)_20px)]",
    icon: Users,
    avatar: "bg-accent text-primary",
    badge: "bg-white/90 text-[#1f2a21]",
  },
  manager: {
    roof: "bg-[linear-gradient(135deg,#2f5d46,#4f8c6a)]",
    pattern:
      "bg-[repeating-linear-gradient(90deg,transparent_0,transparent_18px,rgba(255,255,255,0.12)_18px,rgba(255,255,255,0.12)_20px)]",
    icon: Headset,
    avatar: "bg-accent text-primary",
    badge: "bg-white/90 text-[#1f2a21]",
  },
};

const kindOf = (role: Role | undefined): Kind => {
  if (!role) return "manager";
  if (role.key === "owner" || role.key === "developer" || role.key === "admin")
    return role.key;
  if (role.permissions.includes("leads.all")) return "rop";
  return "manager";
};

const monthStart = () => {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * The team as the windows of a house. Each role has its own window: the
 * owner sees money, the developer the system, the head of sales the team,
 * a manager their own work, the administrator people and access.
 */
export function SalesList() {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [locale] = useLocaleState();
  const { currency } = useConfigurationContext();
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });
  const { data: roles = [] } = useGetList<Role>("roles", {
    pagination: { page: 1, perPage: 100 },
  });
  const { data: deals = [] } = useGetList<DealCard>("deal_cards", {
    filter: { "archived_at@is": null },
    pagination: { page: 1, perPage: 5000 },
  });
  const { data: linked = [] } = useQuery({
    queryKey: ["telegram_people"],
    queryFn: async () => {
      const { data } = await getSupabaseClient().rpc("telegram_people");
      return (data ?? []) as { id: number; linked: boolean }[];
    },
  });
  const { data: month } = useQuery({
    queryKey: ["team_month"],
    queryFn: async () => {
      const { data } = await getSupabaseClient().rpc("owner_overview", {
        since: monthStart().toISOString(),
        prev_since: null,
        days: 1,
        old_tag: null,
        fast_minutes: 15,
        booking_days: 3,
      });
      return data as {
        current: { leads: number; sales: number; sales_amount: number };
        apartments: Partial<Record<string, { count: number }>>;
      } | null;
    },
  });
  const isDeveloper = identity?.roleKey === "developer";
  const { data: health } = useQuery({
    queryKey: ["system_health"],
    queryFn: async () => {
      const { data } = await getSupabaseClient().rpc("system_health");
      return data as {
        errors_24h: number;
        telegram_queue: number;
        cron: { last_status: string | null }[];
      } | null;
    },
    enabled: isDeveloper,
  });

  const stats = useMemo(() => {
    const start = monthStart();
    const map = new Map<unknown, Stats>();
    const total: Stats = { active: 0, noCall: 0, wonMonth: 0 };
    for (const deal of deals) {
      const row = map.get(deal.sales_id) ?? {
        active: 0,
        noCall: 0,
        wonMonth: 0,
      };
      for (const r of [row, total]) {
        if (deal.stage === "won") {
          if (deal.stage_changed_at && new Date(deal.stage_changed_at) >= start)
            r.wonMonth++;
        } else if (deal.stage !== "lost") {
          r.active++;
          if (!deal.first_action_at) r.noCall++;
        }
      }
      map.set(deal.sales_id, row);
    }
    return { map, total };
  }, [deals]);
  const unassigned = stats.map.get(null);
  const money = (value: number) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      notation: value >= 100_000 ? "compact" : "standard",
      maximumFractionDigits: value >= 100_000 ? 1 : 0,
    }).format(value);
  const t = (key: string, args?: Record<string, unknown>) =>
    translate(`crm.team.${key}`, args);

  const order: Kind[] = ["owner", "rop", "manager", "admin", "developer"];
  const people = sales
    .map((sale) => ({
      sale,
      role: roles.find((r) => r.id === sale.role_id),
    }))
    .sort(
      (a, b) =>
        Number(!!a.sale.disabled) - Number(!!b.sale.disabled) ||
        order.indexOf(kindOf(a.role)) - order.indexOf(kindOf(b.role)),
    );
  const linkedCount = linked.filter((p) => p.linked).length;
  const cronOk = health?.cron.every((j) => j.last_status !== "failed");

  const statsFor = (kind: Kind, sale: Sale): [ReactNode, string, string?][] => {
    const own = stats.map.get(sale.id) ?? { active: 0, noCall: 0, wonMonth: 0 };
    switch (kind) {
      case "owner":
        return [
          [
            money(month?.current.sales_amount ?? 0),
            t("s.sold_month"),
            "text-[#8a5a0b]",
          ],
          [month?.current.leads ?? "—", t("s.leads_month")],
          [month?.apartments.free?.count ?? "—", t("s.free_flats")],
        ];
      case "developer":
        return isDeveloper
          ? [
              [
                health?.errors_24h ?? "—",
                t("s.errors"),
                health?.errors_24h ? "text-destructive" : "",
              ],
              [
                cronOk == null ? "—" : cronOk ? "OK" : "FAIL",
                t("s.jobs"),
                cronOk === false ? "text-destructive" : "text-[#17703c]",
              ],
              [health?.telegram_queue ?? "—", t("s.queue")],
            ]
          : [];
      case "admin":
        return [
          [sales.filter((s) => !s.disabled).length, t("s.people")],
          [
            `${linkedCount}/${sales.filter((s) => !s.disabled).length}`,
            t("s.telegram"),
          ],
          [
            roles.filter((r) => r.key !== "admin" && r.key !== "developer")
              .length,
            t("s.roles"),
          ],
        ];
      case "rop":
        return [
          [stats.total.active, t("s.team_active")],
          [
            stats.total.noCall,
            t("s.team_no_call"),
            stats.total.noCall ? "text-destructive" : "",
          ],
          [
            stats.total.wonMonth,
            t("s.team_won"),
            stats.total.wonMonth ? "text-[#17703c]" : "",
          ],
        ];
      default:
        return [
          [own.active, t("stats.active")],
          [
            own.noCall,
            t("stats.no_call"),
            own.noCall ? "text-destructive" : "",
          ],
          [own.wonMonth, t("stats.won"), own.wonMonth ? "text-[#17703c]" : ""],
        ];
    }
  };

  return (
    <FunnelShell title={t("title")}>
      <p className="-mt-2 text-muted-foreground">
        {t("subtitle", { count: sales.filter((s) => !s.disabled).length })}
        {unassigned?.active
          ? ` · ${t("unassigned", { count: unassigned.active })}`
          : ""}
      </p>
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {people.map(({ sale, role }) => {
          const kind = kindOf(role);
          const look = LOOK[kind];
          const Icon = look.icon;
          const tg = linked.find((p) => p.id === sale.id)?.linked;
          const initials =
            `${sale.first_name?.[0] ?? ""}${sale.last_name?.[0] ?? ""}`.toUpperCase();
          const rows = statsFor(kind, sale);
          const dev = kind === "developer";
          return (
            <Link
              key={sale.id}
              to={`/sales/${sale.id}`}
              className={cn(
                "group relative flex flex-col overflow-hidden rounded-3xl border bg-card text-foreground no-underline shadow-sm transition hover:-translate-y-0.5 hover:shadow-md",
                kind === "owner" && "ring-1 ring-[#e3ac45]/50",
                dev && "border-[#17212b]/30",
                sale.disabled && "opacity-55",
              )}
            >
              <div className={cn("relative h-24", look.roof)}>
                <div className={cn("absolute inset-0", look.pattern)} />
                {dev ? (
                  <span className="absolute top-3.5 left-4 font-mono text-[11px] text-white/45">
                    $ oselya --role dev
                  </span>
                ) : null}
                <span
                  className={cn(
                    "absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold",
                    look.badge,
                  )}
                >
                  <Icon className="size-3.5" />
                  {role?.name ?? "—"}
                </span>
              </div>
              <div className="relative z-10 -mt-10 flex flex-1 flex-col items-center px-5 pb-5 text-center">
                <span
                  className={cn(
                    "flex size-20 items-center justify-center overflow-hidden rounded-2xl border-4 border-card text-2xl font-extrabold shadow-sm",
                    look.avatar,
                  )}
                >
                  {sale.avatar?.src ? (
                    <img
                      src={sale.avatar.src}
                      alt=""
                      className="size-full object-cover"
                    />
                  ) : (
                    initials
                  )}
                </span>
                <div
                  className={cn("mt-2 text-lg leading-tight font-extrabold")}
                >
                  {sale.first_name} {sale.last_name}
                  {identity?.id === sale.id ? (
                    <span className="ml-1.5 text-xs font-semibold text-muted-foreground">
                      {t("you")}
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 inline-flex max-w-full items-center gap-1 text-xs text-muted-foreground">
                  <Mail className="size-3 shrink-0" />
                  <span className="truncate">{sale.email}</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t(`about.${kind}`)}
                </p>

                {rows.length ? (
                  <div className="mt-3 grid w-full grid-cols-3 gap-2">
                    {rows.map(([value, label, tone], i) => (
                      <div
                        key={i}
                        className={cn(
                          "rounded-xl px-1 py-2",
                          dev
                            ? "border border-[#2b3540]/10 bg-[#f1f3f4] dark:bg-[#2b3540]/40"
                            : kind === "owner"
                              ? "bg-[#fbf3df]"
                              : "bg-muted/60",
                        )}
                      >
                        <div
                          className={cn(
                            "truncate text-lg font-extrabold tabular-nums",
                            dev && "font-mono",
                            tone,
                          )}
                        >
                          {value}
                        </div>
                        <div
                          className={cn(
                            "text-[11px] leading-tight",
                            "text-muted-foreground",
                          )}
                        >
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="mt-auto flex w-full items-center justify-between pt-3 text-xs">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 font-semibold",
                      tg ? "text-[#2a8bd6]" : "text-muted-foreground",
                    )}
                  >
                    <Send className="size-3.5" />
                    {t(tg ? "tg_on" : "tg_off")}
                  </span>
                  {sale.disabled ? (
                    <span className="rounded-full bg-orange-100 px-2 py-0.5 font-semibold text-orange-800">
                      {translate("resources.sales.fields.disabled")}
                    </span>
                  ) : (
                    <span className="font-semibold text-primary opacity-0 transition group-hover:opacity-100">
                      {t("open")} →
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}

        {/* a free window: invite someone */}
        <Link
          to="/sales/create"
          className="flex min-h-72 flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-primary/30 bg-card/50 p-6 text-center text-foreground no-underline transition hover:border-primary/60 hover:bg-primary/5"
        >
          <span className="flex size-16 items-center justify-center rounded-2xl bg-primary/10">
            <Plus className="size-8 text-primary" />
          </span>
          <span className="text-lg font-extrabold">{t("invite")}</span>
          <span className="max-w-60 text-sm text-muted-foreground">
            {t("invite_hint")}
          </span>
        </Link>
      </div>
    </FunnelShell>
  );
}
